/**
 * WalkthroughCard — Animated card displaying a single walkthrough step.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Network, UserCheck, Activity, TrendingUp, BarChart3,
  LayoutDashboard, Trophy, Target, AlertTriangle, Users, UserCog,
  ClipboardList, CalendarDays, Layers, Ticket, Mail, Route, FileText,
  Globe, Crown, Handshake, Kanban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { WalkthroughStep, AnimationType } from "@/hooks/walkthroughs/types";

const iconMap: Record<string, React.ElementType> = {
  Rocket, Network, UserCheck, Activity, TrendingUp, BarChart3,
  LayoutDashboard, Trophy, Target, AlertTriangle, Users, UserCog,
  ClipboardList, CalendarDays, Layers, Ticket, Mail, Route, FileText,
  Globe, Crown, Handshake, Kanban,
};

function getAnimationVariants(anim: AnimationType) {
  const base = { opacity: 0 };
  const show = { opacity: 1, x: 0, y: 0, scale: 1 };
  switch (anim) {
    case "slide-up": return { initial: { ...base, y: 40 }, animate: show };
    case "slide-down": return { initial: { ...base, y: -40 }, animate: show };
    case "slide-left": return { initial: { ...base, x: 40 }, animate: show };
    case "slide-right": return { initial: { ...base, x: -40 }, animate: show };
    case "scale": return { initial: { ...base, scale: 0.85 }, animate: show };
    default: return { initial: base, animate: show };
  }
}

interface Props {
  step: WalkthroughStep;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

export default function WalkthroughCard({ step, isFirst, isLast, onNext, onPrev, onSkip }: Props) {
  const Icon = step.icon ? iconMap[step.icon] : null;
  const variants = getAnimationVariants(step.animation);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={step.id}
        initial={variants.initial}
        animate={variants.animate}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="bg-card border border-border rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        {/* Icon */}
        {Icon && (
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 15 }}
            className={`mx-auto mb-5 w-14 h-14 rounded-2xl flex items-center justify-center ${
              step.isChapter
                ? "bg-primary text-primary-foreground"
                : "bg-primary/10 text-primary"
            }`}
          >
            <Icon className="w-7 h-7" />
          </motion.div>
        )}

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={`text-center font-bold text-foreground mb-3 ${
            step.isChapter ? "text-2xl" : "text-lg"
          }`}
        >
          {step.title}
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center text-sm text-muted-foreground leading-relaxed mb-6"
        >
          {step.description}
        </motion.p>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center justify-between gap-3"
        >
          <div>
            {!isFirst && (
              <Button variant="ghost" size="sm" onClick={onPrev} type="button">
                {step.prevLabel || "← Back"}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isLast && (
              <Button variant="ghost" size="sm" onClick={onSkip} type="button" className="text-muted-foreground">
                {step.skipLabel || "Skip"}
              </Button>
            )}
            <Button size="sm" onClick={onNext} type="button" className="px-5">
              {step.nextLabel || (isLast ? "Finish ✓" : "Next →")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
